import {combineReducers} from '@reduxjs/toolkit';

import {authReducer} from 'app/auth/slice';
import {commentsSlice} from 'app/comments/slice';
import {documentsReducer} from 'app/documents/slice';
import {foldersSlice} from 'app/folders/slice';
import {loadingBarReducer} from 'app/general/sweet-loading-bar/slice';
import {jobsReducer} from 'app/jobs/slice';
import {kbReducer} from 'app/kbs/reducers';
import {lanesReducer} from 'app/lanes/slice';
import notifications from 'app/notifly/reducers';
import {notiflySlice} from 'app/notifly/slice';
import {sweetaiSlice} from 'app/openai/slice';
import policies from 'app/policies/reducers';
import {policiesReducer} from 'app/policies/slice';
import {presenceReducer} from 'app/presence/reducers';
import proceduresPage from 'app/procedures/reducers';
import processArea from 'app/process/reducers';
import {quizSlice} from 'app/quizzes/slice';
import {receiveRelatedItemsReducer} from 'app/reducers/relatedItems';
import {reportsReducer} from 'app/reports/slice';
import {reviews} from 'app/review/reducers';
import {settingsReducer} from 'app/settings/slice';
import {signoffs} from 'app/signoffs/reducers';
import {subscriptionsReducer} from 'app/subscriptions/slice';
import {tagsSlice} from 'app/tags/slice';
import {tasksReducer} from 'app/tasks/slice';
import teamsArea from 'app/teams/reducers';
import {teamsReducer} from 'app/teams/slice';
import {toastsReducer} from 'app/toasts/slice';
import {usersReducer} from 'app/user/slice';

import {restoreLocaStorageReducer} from './local-storage';
import {sweetAPI} from './sweet-api/sweet-api';

import type {Map} from 'immutable';
import type {AnyAction, Reducer} from 'redux';

// used for all old reducers which are untyped immutable Keys
type ImmutableReducer = Reducer<Map<string, any>>;

const combinedReducers = combineReducers({
  loadingBar: loadingBarReducer,
  tasks: tasksReducer,
  teamsArea: teamsArea as ImmutableReducer,
  teams: teamsReducer,
  users: usersReducer,
  processArea: processArea as ImmutableReducer,
  proceduresPage: proceduresPage as ImmutableReducer,
  tags: tagsSlice.reducer,
  quizzes: quizSlice.reducer,
  toasts: toastsReducer,
  settings: settingsReducer,
  subscriptions: subscriptionsReducer,
  notifications: notifications as ImmutableReducer,
  notifly: notiflySlice.reducer,
  comments: commentsSlice.reducer,
  sweetai: sweetaiSlice.reducer,
  policiesArea: policies as ImmutableReducer,
  policies: policiesReducer,
  reviews: reviews as ImmutableReducer,
  reports: reportsReducer,
  signoffs: signoffs as ImmutableReducer,
  folders: foldersSlice.reducer,
  documents: documentsReducer,
  lanes: lanesReducer,
  presence: presenceReducer as ImmutableReducer,
  kbs: kbReducer as Reducer<{
    knowledgebases: Map<string, any>;
    categories: Map<string, any>;
    questions: Map<string, any>;
    feedback: Map<string, any>;
  }>,
  userjobs: jobsReducer,
  auth: authReducer,
  [sweetAPI.reducerPath]: sweetAPI.reducer,
});

export type RootState = ReturnType<typeof combinedReducers>;

const rootReducer = (state: RootState, action: AnyAction): RootState => {
  const restoredState = restoreLocaStorageReducer(state, action);
  const stateWithRelatedItems = receiveRelatedItemsReducer(
    restoredState,
    action
  );
  return combinedReducers(stateWithRelatedItems, action);
};

export default rootReducer;
